// Org settings — domains, join code, retention policies (R-17–R-21, R-178, R-64).

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { rotateJoinCode, addDomain } from '../lib/api';

export function SettingsPage() {
  const { user } = useAuth();
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [dnsToken, setDnsToken] = useState<string | null>(null);
  const [addingDomain, setAddingDomain] = useState(false);

  // In a real build, load org details here including current join code.
  useEffect(() => { setJoinCode('ANCHOR-XXXX'); }, []);

  if (!user) return null;

  async function handleRotateCode() {
    if (!confirm('Rotate the join code? The old code stops working immediately.')) return;
    setRotating(true);
    try {
      const { joinCode: code } = await rotateJoinCode(user!.orgId);
      setJoinCode(code);
    } finally {
      setRotating(false);
    }
  }

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    try {
      const { dnsToken: token } = await addDomain(user!.orgId, newDomain.trim());
      setDnsToken(token);
      setNewDomain('');
    } finally {
      setAddingDomain(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">{user.orgName} configuration</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: '640px' }}>

        {/* Join code */}
        <section className="card">
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-4)' }}>Join code</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
            Share this code with people you'd like to invite. They submit a join request;
            an admin approves it. Rotate any time — the old code stops working immediately (R-21).
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '1.25rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                background: 'var(--color-surface-alt)',
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--color-border)',
              }}
            >
              {joinCode ?? '—'}
            </code>
            <button
              className="btn btn--secondary btn--sm"
              onClick={handleRotateCode}
              disabled={rotating}
            >
              {rotating ? 'Rotating…' : 'Rotate code'}
            </button>
          </div>
        </section>

        {/* Verified domains */}
        <section className="card">
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-2)' }}>Verified email domains</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
            Users signing in with a verified domain are automatically approved.
            Apple Private Relay addresses (@privaterelay.appleid.com) never match
            a domain — those users must use the join code or invitation path (R-16).
          </p>

          <form onSubmit={handleAddDomain} style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="yourorg.com"
              style={{ flex: 1, minWidth: '200px' }}
              aria-label="Email domain to verify"
            />
            <button type="submit" className="btn btn--primary btn--sm" disabled={addingDomain || !newDomain.trim()}>
              {addingDomain ? 'Adding…' : 'Add domain'}
            </button>
          </form>

          {dnsToken && (
            <div
              className="card"
              style={{ marginTop: 'var(--space-4)', background: 'var(--color-warning-light)', border: '1px solid #fbbf24' }}
            >
              <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                DNS verification required
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
                Add this TXT record to your DNS to verify ownership:
              </p>
              <code
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.8125rem',
                  background: 'var(--color-surface)',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  wordBreak: 'break-all',
                }}
              >
                anchor-verify={dnsToken}
              </code>
            </div>
          )}
        </section>

        {/* Retention */}
        <section className="card">
          <h2 style={{ fontSize: '1rem', marginBottom: 'var(--space-2)' }}>Data retention</h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
            Per-category retention settings. Configured here; enforced by server-side purge jobs (R-146/R-147).
          </p>

          <RetentionRow label="Coach transcripts (R-40)" defaultValue="90" options={['30', '90', '365', 'forever']} />
          <RetentionRow label="Engine Check entries (R-178)" defaultValue="90" options={['30', '90', '180']} />
          <RetentionRow label="Team feed (R-64)" defaultValue="180" options={['90', '180', '365']} />
        </section>

      </div>
    </>
  );
}

function RetentionRow({
  label,
  defaultValue,
  options,
}: {
  label: string;
  defaultValue: string;
  options: string[];
}) {
  const [value, setValue] = useState(defaultValue);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-4)',
        padding: 'var(--space-3) 0',
        borderBottom: '1px solid var(--color-border)',
        flexWrap: 'wrap',
      }}
    >
      <label
        style={{ fontSize: '0.9375rem' }}
        htmlFor={`retention-${label}`}
      >
        {label}
      </label>
      <select
        id={`retention-${label}`}
        className="form-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={{ width: 'auto', padding: '4px var(--space-3)' }}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o === 'forever' ? 'Forever' : `${o} days`}
          </option>
        ))}
      </select>
    </div>
  );
}
